import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/tokenStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDERS = [
  { key: '{first_name}', label: 'First name' },
  { key: '{last_name}', label: 'Last name' },
  { key: '{full_name}', label: 'Full name' },
  { key: '{email}', label: 'Email' },
  { key: '{username}', label: 'Username' },
  { key: '{membership_type}', label: 'Membership type' },
  { key: '{membership_class}', label: 'Membership class' },
  { key: '{membership_number}', label: 'Member #' },
  { key: '{status}', label: 'Status' },
  { key: '{suburb}', label: 'Suburb' },
  { key: '{state}', label: 'State' },
  { key: '{phone}', label: 'Phone' },
];

const PlaceholderHelp = ({ onInsert }) => (
  <div style={{ background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#4b5563', marginBottom: '12px' }}>
    <div style={{ fontWeight: 600, marginBottom: '6px', color: '#111827' }}>Click to insert a placeholder:</div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {PLACEHOLDERS.map(p => (
        <button key={p.key} type="button" onClick={() => onInsert(p.key)}
          style={{ padding: '2px 8px', borderRadius: '12px', border: '1px solid #d1d5db', background: 'white', fontSize: '11px', cursor: 'pointer', color: '#374151' }}
          title={p.label} data-testid={`placeholder-${p.key.replace(/[{}]/g, '')}`}>
          {p.key}
        </button>
      ))}
    </div>
  </div>
);

const TemplateEditorRow = ({ t, onSaved }) => {
  const [subject, setSubject] = useState(t.subject);
  const [body, setBody] = useState(t.body);
  const [saving, setSaving] = useState(false);
  const dirty = subject !== t.subject || body !== t.body;

  const save = async () => {
    setSaving(true);
    const token = getToken();
    try {
      const { data } = await axios.put(`${API}/admin/message-templates/${t.template_key}`, { subject, body },
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      setSubject(data.subject); setBody(data.body);
      onSaved(data);
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const resetDefault = async () => {
    if (!window.confirm(`Reset "${t.label}" back to the original HSS wording?`)) return;
    const token = getToken();
    try {
      const { data } = await axios.post(`${API}/admin/message-templates/${t.template_key}/reset`, {},
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      setSubject(data.subject); setBody(data.body);
      onSaved(data);
    } catch { alert('Reset failed'); }
  };

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', marginBottom: '14px' }} data-testid={`template-editor-${t.template_key}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <strong style={{ color: '#1a2332' }}>{t.label}</strong>
        <span style={{ fontSize: '11px', color: t.is_default ? '#9ca3af' : '#059669', fontWeight: 600 }}>
          {t.is_default ? 'Default wording' : 'Edited'}
        </span>
      </div>
      <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }}
        data-testid={`template-subject-${t.template_key}`} />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5}
        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box', resize: 'vertical' }}
        data-testid={`template-body-${t.template_key}`} />
      <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>
        Available: {t.placeholders.map(p => `{${p}}`).join(', ')}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="button" className="btn btn-primary btn-sm" disabled={!dirty || saving} onClick={save} data-testid={`template-save-${t.template_key}`}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {!t.is_default && (
          <button type="button" className="btn btn-sm" onClick={resetDefault} data-testid={`template-reset-${t.template_key}`}>Reset to Default</button>
        )}
      </div>
    </div>
  );
};

const AdminAnnouncementsTab = ({ members, announcements, announcementForm, setAnnouncementForm, individualMsg, setIndividualMsg, onRefresh }) => {
  const [templates, setTemplates] = useState([]);
  const [showManager, setShowManager] = useState(false);

  const fetchTemplates = async () => {
    const token = getToken();
    try {
      const { data } = await axios.get(`${API}/admin/message-templates`, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      setTemplates(data);
    } catch { /* non-fatal: dropdowns just show nothing to pick */ }
  };
  useEffect(() => { fetchTemplates(); }, []);

  const broadcastTemplates = templates.filter(t => t.category === 'broadcast_preset');
  const paymentReminderTemplate = templates.find(t => t.template_key === 'payment_reminder');

  const applyTemplate = (which, target) => {
    const t = templates.find(x => x.template_key === which);
    if (!t) return;
    if (target === 'broadcast') setAnnouncementForm({ ...announcementForm, subject: t.subject, body: t.body });
    else setIndividualMsg({ ...individualMsg, subject: t.subject, message: t.body });
  };
  const insertIntoBroadcast = (placeholder) => setAnnouncementForm({ ...announcementForm, body: (announcementForm.body || '') + placeholder });
  const insertIntoIndividual = (placeholder) => setIndividualMsg({ ...individualMsg, message: (individualMsg.message || '') + placeholder });

  const handleTemplateSaved = (updated) => {
    setTemplates(prev => prev.map(t => t.template_key === updated.template_key ? updated : t));
  };

  return (
    <div className="dashboard-content" data-testid="announcements-section">
      <div className="section-header" style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2>Announcements &amp; Messaging</h2>
          <p>Send broadcasts to all members or message individuals. Use <code>{'{first_name}'}</code>, <code>{'{suburb}'}</code> etc. to personalise each email.</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => setShowManager(v => !v)} data-testid="manage-templates-btn">
          {showManager ? 'Close Template Library' : 'Manage Message Templates'}
        </button>
      </div>

      {showManager && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px', gridColumn: '1 / -1' }} data-testid="message-templates-library">
          <h3 style={{ margin: '0 0 6px', color: '#1a2332' }}>Message Template Library</h3>
          <p style={{ fontSize: '13px', color: '#6b7280', marginTop: 0, marginBottom: '16px' }}>
            Edit the wording used by the "Insert a template…" presets below, and the automated Payment Reminder email sent to approved members who haven't paid.
            Other system emails (approvals, rejections, security codes, internal alerts) are not part of this library.
          </p>
          <h4 style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', margin: '0 0 10px' }}>Broadcast / Individual Message Presets</h4>
          {broadcastTemplates.map(t => <TemplateEditorRow key={t.template_key} t={t} onSaved={handleTemplateSaved} />)}
          <h4 style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', margin: '18px 0 10px' }}>Automated Email</h4>
          {paymentReminderTemplate && <TemplateEditorRow t={paymentReminderTemplate} onSaved={handleTemplateSaved} />}
        </div>
      )}

      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', gridColumn: '1 / -1' }}>
        <div style={{ flex: 1, minWidth: '300px', background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px', color: '#1a2332' }}>Broadcast to All Members</h3>
          <select onChange={(e) => { if (e.target.value) { applyTemplate(e.target.value, 'broadcast'); e.target.value = ''; } }} defaultValue=""
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', marginBottom: '10px' }}
            data-testid="broadcast-template-select">
            <option value="">Insert a template…</option>
            {broadcastTemplates.map(t => <option key={t.template_key} value={t.template_key}>{t.label}</option>)}
          </select>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const token = getToken();
            try {
              await axios.post(`${API}/admin/announcements`, announcementForm, { withCredentials: true, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
              alert('Announcement sent!');
              setAnnouncementForm({ subject: '', body: '', send_email: true });
              onRefresh();
            } catch { alert('Failed to send'); }
          }}>
            <input type="text" placeholder="Subject" required value={announcementForm.subject} onChange={(e) => setAnnouncementForm({ ...announcementForm, subject: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' }} data-testid="announcement-subject" />
            <PlaceholderHelp onInsert={insertIntoBroadcast} />
            <textarea placeholder="Message body..." required rows={6} value={announcementForm.body} onChange={(e) => setAnnouncementForm({ ...announcementForm, body: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box', resize: 'vertical' }} data-testid="announcement-body" />
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '14px', cursor: 'pointer' }}>
              <input type="checkbox" checked={announcementForm.send_email} onChange={(e) => setAnnouncementForm({ ...announcementForm, send_email: e.target.checked })} data-testid="announcement-email-toggle" />
              Also send via email
            </label>
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} data-testid="send-announcement-btn">Send to All Members</button>
          </form>
        </div>
        <div style={{ flex: 1, minWidth: '300px', background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px', color: '#1a2332' }}>Message Individual Member</h3>
          <select onChange={(e) => { if (e.target.value) { applyTemplate(e.target.value, 'individual'); e.target.value = ''; } }} defaultValue=""
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', marginBottom: '10px' }}
            data-testid="individual-template-select">
            <option value="">Insert a template…</option>
            {broadcastTemplates.map(t => <option key={t.template_key} value={t.template_key}>{t.label}</option>)}
          </select>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const token = getToken();
            try {
              await axios.post(`${API}/admin/send-individual`, individualMsg, { withCredentials: true, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
              alert('Message sent!');
              setIndividualMsg({ user_id: '', subject: '', message: '' });
            } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
          }}>
            <select required value={individualMsg.user_id} onChange={(e) => setIndividualMsg({ ...individualMsg, user_id: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' }} data-testid="individual-member-select">
              <option value="">Select Member...</option>
              {members.map(m => <option key={m.user_id} value={m.user_id}>{m.name} ({m.email})</option>)}
            </select>
            <input type="text" placeholder="Subject" required value={individualMsg.subject} onChange={(e) => setIndividualMsg({ ...individualMsg, subject: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' }} data-testid="individual-subject" />
            <PlaceholderHelp onInsert={insertIntoIndividual} />
            <textarea placeholder="Message..." required rows={5} value={individualMsg.message} onChange={(e) => setIndividualMsg({ ...individualMsg, message: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box', resize: 'vertical' }} data-testid="individual-message" />
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} data-testid="send-individual-btn">Send Message</button>
          </form>
        </div>
      </div>
      {announcements.length > 0 && (
        <div style={{ marginTop: '24px', gridColumn: '1 / -1' }}>
          <h3 style={{ color: '#1a2332', marginBottom: '12px' }}>Sent Announcements</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '42%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '26%' }} />
              </colgroup>
              <thead><tr><th style={{ whiteSpace: 'nowrap' }}>Subject</th><th style={{ whiteSpace: 'nowrap' }}>Recipients</th><th style={{ whiteSpace: 'nowrap' }}>Email</th><th style={{ whiteSpace: 'nowrap' }}>Date</th></tr></thead>
              <tbody>
                {announcements.map(a => (
                  <tr key={a.announcement_id}>
                    <td style={{ verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 700, color: '#1a2332', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.subject}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', maxHeight: '36px' }}>{a.body}</div>
                    </td>
                    <td style={{ verticalAlign: 'top' }}>{a.recipient_count}</td>
                    <td style={{ verticalAlign: 'top' }}>{a.sent_email ? 'Yes' : 'No'}</td>
                    <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>{new Date(a.created_at).toLocaleDateString('en-AU')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnnouncementsTab;
